import type { ComponentProps } from 'react';
import React from 'react';
import { BodyL, HeadingXL } from '../typography';
import type { IBreadcrumbItem } from '../breadcrumb';
import { Breadcrumb } from '../breadcrumb';
import { cn, Icon } from '@lqa-frontend/ui-system';

type PageHeaderProps = ComponentProps<'header'> & {
	breadcrumbItems?: IBreadcrumbItem[];
	onBreadcrumbClick?: (item: IBreadcrumbItem) => void;
	childrenClassName?: string;
};

function PageHeader({
	breadcrumbItems,
	onBreadcrumbClick,
	children,
	className,
	childrenClassName,
	...props
}: PageHeaderProps) {
	return (
		<header
			className={cn('mb-20 flex flex-col', className)}
			data-testid="page-header"
			{...props}
		>
			{breadcrumbItems && breadcrumbItems.length > 0 && (
				<div className="mb-16">
					<Breadcrumb items={breadcrumbItems} onItemClick={onBreadcrumbClick} />
				</div>
			)}
			<div
				className={cn(
					'flex flex-col gap-gutter tablet:gap-16 tablet:flex-row tablet:flex-wrap justify-between items-start pt-12 tablet:pt-24 pb-8 tablet:pb-20',
					childrenClassName
				)}
			>
				{children}
			</div>
		</header>
	);
}
/* ---------- Left side ---------- */

type PageHeaderContentProps = ComponentProps<'div'> & {
	onBack?: () => void;
	shouldShowBackButton?: boolean;
};

function PageHeaderContent({
	onBack,
	shouldShowBackButton,
	children,
	className,
	...props
}: PageHeaderContentProps) {
	if (onBack && shouldShowBackButton !== false) {
		return (
			<div className={`flex items-center gap-8 ${className || ''}`} {...props}>
				<button
					type="button"
					onClick={onBack}
					className="flex items-center justify-center cursor-pointer h-36"
					aria-label="Go back"
					data-testid="page-header-back-button"
				>
					<Icon name="chevronLeft" />
				</button>
				<div className="flex flex-col gap-8">{children}</div>
			</div>
		);
	}

	return (
		<div className={`flex flex-col gap-8 ${className || ''}`} {...props}>
			{children}
		</div>
	);
}

type PageHeaderTitleProps = Omit<ComponentProps<typeof HeadingXL>, 'color'>;

function PageHeaderTitle({ children, ...props }: PageHeaderTitleProps) {
	return (
		<HeadingXL color="primary" data-testid="page-header-title" {...props}>
			{children}
		</HeadingXL>
	);
}

type PageHeaderSubtitleProps = Omit<ComponentProps<typeof BodyL>, 'color'>;

function PageHeaderSubtitle({ children, ...props }: PageHeaderSubtitleProps) {
	return (
		<BodyL color="primary" data-testid="page-header-subtitle" {...props}>
			{children}
		</BodyL>
	);
}

/* ---------- Right side ---------- */

function PageHeaderActions(props: ComponentProps<'div'>) {
	return (
		<div
			className="flex flex-wrap items-center gap-10"
			data-testid="page-header-actions"
			{...props}
		/>
	);
}

export {
	PageHeader,
	PageHeaderContent,
	PageHeaderTitle,
	PageHeaderSubtitle,
	PageHeaderActions,
};

export type { IBreadcrumbItem } from '../breadcrumb';
